import { Play, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

// ============================================================================
// Tutorials — YouTube-backed walkthroughs.
//
// ▶ TO ADD A VIDEO: paste its YouTube id into `youtubeId` below and set the real
//   `duration`. The id is the v= parameter — for
//   https://www.youtube.com/watch?v=dQw4w9WgXcQ  the id is  dQw4w9WgXcQ
//
// An entry with youtubeId: null renders as "Coming soon" with its play button
// disabled. That is deliberate — an enabled play button that does nothing reads
// as broken, and a stated duration for a video that doesn't exist is a claim we
// can't back. Durations are left null until the real video is attached rather
// than carrying over the placeholder values that used to be hard-coded here.
// ============================================================================

const TUTORIALS = [
  {
    label: '[ Setup - 01 ]',
    title: 'Spinning up your first staging site',
    description: 'Create your first hosted WordPress staging site and open the admin.',
    youtubeId: null,
    duration: null,
  },
  {
    label: '[ Products - 02 ]',
    // NOTE: the linked video is "How To Add Product Variations & Options" —
    // narrower than this slot's original title. Retitled to match what it
    // actually shows; a first-product walkthrough may still be worth its own.
    title: 'Adding product variations & options',
    description: 'Set up a product with variations and options, and preview it on staging.',
    youtubeId: 'qwjgxOBn49E',
    duration: null,
  },
  {
    label: '[ Payments - 03 ]',
    title: 'Connecting Stripe in one click',
    description: 'Connect payments before going live with your custom domain.',
    youtubeId: 'wrl_5MDoeoA',
    duration: null,
  },
  {
    label: '[ Domain - 04 ]',
    title: 'Pointing your domain & going live',
    description: 'Publish staging, configure DNS, and start billing for a live site.',
    youtubeId: null,
    duration: null,
  },
  {
    label: '[ Content - 05 ]',
    title: 'Embedding products in blog posts',
    description: 'Use product blocks inside editorial content and landing pages.',
    youtubeId: null,
    duration: null,
  },
  {
    label: '[ Ops - 06 ]',
    title: 'Tracking orders & refunds',
    description: 'Review orders, refunds, and customer activity after launch.',
    youtubeId: null,
    duration: null,
  },
];

// The featured walkthrough at the top of the page.
const FEATURED = {
  title: 'The 8-minute North Commerce walkthrough.',
  description: 'Learn the full path from free staging site to custom-domain production store.',
  // youtu.be/39gLdo-4q2Q — the short-link slug IS the video id.
  youtubeId: '39gLdo-4q2Q',
  duration: null,
};

// youtube-nocookie serves the same player without setting tracking cookies until
// playback starts. rel=0 keeps end-screen suggestions to the same channel.
const embedUrl = (id) =>
  `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`;

// YouTube generates these for every video — no API key, no stored asset.
// hqdefault exists for all videos; maxresdefault does not.
const thumbUrl = (id) => `https://img.youtube.com/vi/${id}/hqdefault.jpg`;

// ── Player modal ────────────────────────────────────────────────────────────
function VideoModal({ video, onClose }) {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  // Whatever was focused before opening, so focus can be handed back on close.
  const openerRef = useRef(null);

  useEffect(() => {
    openerRef.current = document.activeElement;
    closeRef.current?.focus();

    // The page behind a modal must not scroll with it.
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      // Keep Tab inside the dialog — without this, focus walks onto the page
      // behind the overlay where it can't be seen.
      const focusable = dialogRef.current?.querySelectorAll(
        'button, [href], iframe, input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      // Return focus to the card that opened this, not the top of the page.
      if (openerRef.current instanceof HTMLElement) openerRef.current.focus();
    };
  }, [onClose]);

  return (
    <div
      className="video-modal-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="video-modal"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={video.title}
        // Clicks inside the dialog must not reach the backdrop's close handler.
        onClick={(e) => e.stopPropagation()}
      >
        <div className="video-modal-head">
          <h2>{video.title}</h2>
          <button
            className="icon-button"
            type="button"
            onClick={onClose}
            ref={closeRef}
            aria-label="Close video"
          >
            <X size={16} />
          </button>
        </div>
        <div className="video-modal-frame">
          <iframe
            src={embedUrl(video.youtubeId)}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
}

// ── Card ────────────────────────────────────────────────────────────────────
function TutorialCard({ tutorial, index, onPlay }) {
  const { label, title, description, youtubeId, duration } = tutorial;
  const ready = Boolean(youtubeId);

  return (
    <article className={`tutorial-card ${ready ? '' : 'is-pending'}`}>
      <div
        className={`tutorial-thumb thumb-${index % 5}`}
        style={ready ? { backgroundImage: `url(${thumbUrl(youtubeId)})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
      >
        <button
          className="play-button"
          type="button"
          onClick={() => ready && onPlay(tutorial)}
          disabled={!ready}
          aria-label={ready ? `Play ${title}` : `${title} — coming soon`}
          title={ready ? undefined : 'Video coming soon'}
        >
          <Play size={18} fill="currentColor" />
        </button>
        {duration ? <span>{duration}</span> : <span className="is-pending-tag">Coming soon</span>}
      </div>
      <div className="tutorial-body">
        <p>{label}</p>
        <h2>{title}</h2>
        <span>{description}</span>
      </div>
    </article>
  );
}

export default function TutorialsPage() {
  const [playing, setPlaying] = useState(null);
  const featuredReady = Boolean(FEATURED.youtubeId);

  const close = useCallback(() => setPlaying(null), []);

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Tutorials</h1>
          <div className="page-subtitle">Walkthroughs for creating, publishing, and operating hosted stores.</div>
        </div>
      </div>

      <section className="tutorial-hero">
        <div>
          <span>Start here</span>
          <h2>{FEATURED.title}</h2>
          <p>{FEATURED.description}</p>
          <div>
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => featuredReady && setPlaying(FEATURED)}
              disabled={!featuredReady}
              title={featuredReady ? undefined : 'Video coming soon'}
            >
              <Play size={15} fill="currentColor" />
              {featuredReady ? `Watch${FEATURED.duration ? ` - ${FEATURED.duration}` : ''}` : 'Coming soon'}
            </button>
            <button className="btn btn-ghost dark" type="button">Read docs version</button>
          </div>
        </div>
        <div
          className="hero-video"
          style={featuredReady ? { backgroundImage: `url(${thumbUrl(FEATURED.youtubeId)})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
        >
          <button
            className="hero-play"
            type="button"
            onClick={() => featuredReady && setPlaying(FEATURED)}
            disabled={!featuredReady}
            aria-label={featuredReady ? 'Play walkthrough' : 'Walkthrough coming soon'}
          >
            <Play size={24} fill="currentColor" />
          </button>
        </div>
      </section>

      <section className="tutorial-grid">
        {TUTORIALS.map((tutorial, index) => (
          <TutorialCard
            tutorial={tutorial}
            index={index}
            key={tutorial.title}
            onPlay={setPlaying}
          />
        ))}
      </section>

      {playing ? <VideoModal video={playing} onClose={close} /> : null}
    </>
  );
}
